/**
 * Le dessin isométrique du chargement — la géométrie, hors de React.
 *
 * Ce fichier ne décide de rien : il PROJETTE ce que `tournee.ts` a rangé. Deux
 * fichiers plutôt qu'un, parce que la première faute du 04/09/2026 a été de
 * mélanger une convention de comptage et une géométrie de dessin. Le mètre
 * linéaire est une convention ; ce qu'on voit ici est une image.
 *
 *
 * LA 3D CSS A ÉTÉ ESSAYÉE ET ABANDONNÉE
 * ---------------------------------------------------------------------------
 * `transform-style: preserve-3d` faisait disparaître les faces latérales :
 * `backface-visibility` les écartait selon l'ordre des rotations, et le
 * résultat changeait d'un navigateur à l'autre. **Ce qui marche, c'est la
 * projection isométrique en SVG, calculée sommet par sommet** : déterministe,
 * mesurable hors ligne, sans bibliothèque ni CDN — le dépôt n'en charge
 * aucune, et ce n'est pas un dessin qui va commencer.
 *
 *
 * LE TRI EN PEINTRE EST OBLIGATOIRE
 * ---------------------------------------------------------------------------
 * Le SVG n'a pas de profondeur : ce qui est écrit en dernier recouvre le
 * reste. On trie donc par `x + y` croissant — du fond de la remorque vers les
 * portes — puis par étage, pour qu'une palette gerbée passe par-dessus celle
 * qui la porte. Sans ce tri, une palette du fond se dessine par-dessus celle
 * de devant et l'image devient incompréhensible.
 *
 *
 * TROIS FACES PAR BOÎTE, PAS SIX
 * ---------------------------------------------------------------------------
 * Sous cet angle, on ne voit jamais que le dessus, l'avant et le côté droit.
 * Dessiner les trois autres coûterait le double de sommets pour un résultat
 * strictement identique.
 */

import type { Unite } from './tournee.js';

// ---------------------------------------------------------------------------
// La projection
// ---------------------------------------------------------------------------

/** Le côté d'un mètre, en unités du repère SVG. */
export const ECHELLE = 26;

const CX = Math.cos(Math.PI / 6);
const CY = Math.sin(Math.PI / 6);

/** Un point du plan du dessin. */
export type Point = readonly [number, number];

/**
 * Projeter un point de l'espace sur le plan du dessin.
 *
 * `x` va du fond vers les portes, `y` de gauche à droite, `z` vers le haut.
 * **`z` est SOUSTRAIT** : dans un SVG l'ordonnée descend, donc monter dans la
 * remorque, c'est diminuer `y` à l'écran. L'oublier retourne la remorque.
 */
export function projeter(x: number, y: number, z: number): Point {
  return [(x - y) * CX * ECHELLE, ((x + y) * CY - z) * ECHELLE];
}

/** Les points d'un polygone, tels que l'attribut `points` du SVG les attend. */
export function points(...sommets: readonly Point[]): string {
  return sommets.map(([x, y]) => `${x.toFixed(1)},${y.toFixed(1)}`).join(' ');
}

// ---------------------------------------------------------------------------
// Ce qu'on rend à React
// ---------------------------------------------------------------------------

/** Une face à peindre. `classe` dit sa teinte ; l'ordre du tableau fait foi. */
export interface Face {
  readonly points: string;
  /** `'dessus' | 'avant' | 'droite'`. */
  readonly cote: 'dessus' | 'avant' | 'droite';
  /** Le rang du lot dont vient l'unité, pour la couleur. */
  readonly lot: number;
  /** L'arrêt de chargement dont vient l'unité, pour la mise en avant au survol. */
  readonly arret_id: string;
  /** Vrai quand l'unité ne rentre pas. */
  readonly enTrop: boolean;
}

/** Un trait du cadre de la remorque. */
export interface Trait {
  readonly x1: number;
  readonly y1: number;
  readonly x2: number;
  readonly y2: number;
}

/** Tout ce qu'il faut pour peindre le panneau. */
export interface Dessin {
  /** Le plancher, quand la remorque est connue. */
  readonly sol: string | null;
  /** Le rectangle du haut de la caisse. */
  readonly plafond: string | null;
  /** Les quatre montants. */
  readonly montants: readonly Trait[];
  /** Le panneau des portes, à l'arrière. */
  readonly portes: string | null;
  /** Les faces des unités, DANS L'ORDRE OÙ IL FAUT LES PEINDRE. */
  readonly faces: readonly Face[];
  /** Le cadre du SVG, prêt pour l'attribut `viewBox`. */
  readonly viewBox: string;
}

/** Les dimensions intérieures de la remorque, en mètres. */
export interface Caisse {
  readonly longueurM: number;
  readonly largeurM: number;
  readonly hauteurM: number;
}

/** La marge autour du dessin, en unités du repère SVG. */
const MARGE = 10;

/** Les trois faces visibles d'un pavé posé en (x, y, z). */
function facesDuPave(
  x: number,
  y: number,
  z: number,
  profondeur: number,
  travers: number,
  hauteur: number,
): readonly { cote: Face['cote']; points: string }[] {
  const x2 = x + profondeur;
  const y2 = y + travers;
  const z2 = z + hauteur;
  return [
    {
      cote: 'droite',
      points: points(
        projeter(x2, y, z2),
        projeter(x2, y2, z2),
        projeter(x2, y2, z),
        projeter(x2, y, z),
      ),
    },
    {
      cote: 'avant',
      points: points(
        projeter(x, y2, z2),
        projeter(x2, y2, z2),
        projeter(x2, y2, z),
        projeter(x, y2, z),
      ),
    },
    {
      cote: 'dessus',
      points: points(
        projeter(x, y, z2),
        projeter(x2, y, z2),
        projeter(x2, y2, z2),
        projeter(x, y2, z2),
      ),
    },
  ];
}

/**
 * Où une unité se trouve dans l'espace, en mètres.
 *
 * L'étage se traduit en hauteur ici et nulle part ailleurs : `ranger` compte
 * des étages, le dessin compte des mètres.
 */
function situer(unite: Unite): { x: number; y: number; z: number } {
  return { x: unite.x, y: unite.y, z: unite.etage * unite.hauteur };
}

/**
 * Le dessin complet.
 *
 * `caisse` vaut `null` tant qu'aucune remorque n'est attelée : **on dessine
 * alors le chargement SANS remorque**. Inventer une caisse par défaut
 * laisserait croire que ça rentre, alors que la capacité est inconnue.
 *
 * Le cadre du SVG est calculé des sommets réellement projetés — caisse
 * comprise quand elle existe, unités en trop comprises toujours. Un cadre posé
 * en dur couperait ce qui dépasse, c'est-à-dire précisément ce qu'on veut
 * montrer.
 */
export function dessiner(
  unites: readonly Unite[],
  caisse: Caisse | null,
): Dessin {
  const xs: number[] = [];
  const ys: number[] = [];
  const retenir = (p: Point): void => {
    xs.push(p[0]);
    ys.push(p[1]);
  };

  let sol: string | null = null;
  let plafond: string | null = null;
  let portes: string | null = null;
  const montants: Trait[] = [];

  if (caisse !== null) {
    const { longueurM: L, largeurM: l, hauteurM: h } = caisse;
    const coins: readonly Point[] = [
      [0, 0],
      [L, 0],
      [L, l],
      [0, l],
    ];
    const bas = coins.map(([x, y]) => projeter(x, y, 0));
    const haut = coins.map(([x, y]) => projeter(x, y, h));

    sol = points(...bas);
    plafond = points(...haut);
    portes = points(
      projeter(L, 0, h),
      projeter(L, l, h),
      projeter(L, l, 0),
      projeter(L, 0, 0),
    );

    for (let i = 0; i < 4; i += 1) {
      const b = bas[i];
      const t = haut[i];
      if (b === undefined || t === undefined) continue;
      montants.push({ x1: b[0], y1: b[1], x2: t[0], y2: t[1] });
      retenir(b);
      retenir(t);
    }
  }

  // LE TRI EN PEINTRE. `x + y` d'abord — du fond vers les portes — puis
  // l'étage, pour qu'une palette gerbée passe par-dessus son support. `sort`
  // est stable depuis ES2019 : à position égale, l'ordre de rangement tranche,
  // et deux affichages de la même tournée donnent exactement la même image.
  const ordonnees = [...unites].sort((a, b) => {
    const pa = a.x + a.y;
    const pb = b.x + b.y;
    if (pa !== pb) return pa - pb;
    return a.etage - b.etage;
  });

  const faces: Face[] = [];
  for (const unite of ordonnees) {
    const { x, y, z } = situer(unite);
    // Un jeu de 2 cm entre les unités : collées, leurs arêtes se confondent et
    // vingt palettes ne se distinguent plus les unes des autres.
    const profondeur = Math.max(0.01, unite.profondeur - 0.02);
    const travers = Math.max(0.01, unite.travers - 0.02);
    for (const face of facesDuPave(x, y, z, profondeur, travers, unite.hauteur)) {
      faces.push({
        points: face.points,
        cote: face.cote,
        lot: unite.lot,
        arret_id: unite.arret_id,
        enTrop: unite.enTrop,
      });
    }
    for (const cx of [x, x + profondeur]) {
      for (const cy of [y, y + travers]) {
        for (const cz of [z, z + unite.hauteur]) {
          retenir(projeter(cx, cy, cz));
        }
      }
    }
  }

  // Un dessin vide sans remorque n'a aucun sommet : un `viewBox` calculé de
  // rien donnerait `NaN`, et le SVG entier disparaîtrait sans message.
  if (xs.length === 0 || ys.length === 0) {
    return { sol, plafond, montants, portes, faces, viewBox: '0 0 1 1' };
  }

  const x0 = Math.min(...xs) - MARGE;
  const y0 = Math.min(...ys) - MARGE;
  const largeur = Math.max(...xs) - Math.min(...xs) + 2 * MARGE;
  const hauteur = Math.max(...ys) - Math.min(...ys) + 2 * MARGE;

  return {
    sol,
    plafond,
    montants,
    portes,
    faces,
    viewBox: `${x0.toFixed(1)} ${y0.toFixed(1)} ${largeur.toFixed(1)} ${hauteur.toFixed(1)}`,
  };
}
